// app/src/features/branch/CustomEventPopover.jsx
import React from "react";
import TriggerDepthSelect from "../../components/TriggerDepthSelect.jsx";
import { C } from "../../lib/colors.js";
import { customEventPopoverBox } from "../../lib/customEventPopoverBox.js";
import { customEventPopoverEdited } from "../../lib/forecast/reducer.js";
import { surface, ui } from "../../lib/telemetry/instrument.js";

/**
 * The "add a trigger" popover: the typed custom event, as a surface of the forecast it was opened
 * on (its slot in that run's store state, `customEvtPopover` / `customEvtPopoverRunId` —
 * AppInner's, derived from `runState`). Rendered beside `AppFrame` (not a frame slot), after the
 * delete-forecast confirm as the inline JSX was, for the phone and the desktop layouts alike; the
 * handlers (`useCustomEventPopover`'s, through `useBranchWiring`), `uiZoom`, the orchestrators and
 * the store's dispatcher come from the frame context. The inputs stay uncontrolled: what is typed
 * is kept on the run by the hook when the popover leaves the screen, never written per keystroke.
 */
export default function CustomEventPopover({ ctx }) {
  const {
    customEvtPopover,
    customEvtPopoverRunId,
    customEvtDescriptionRef,
    customEvtDateRef,
    closeCustomEventPopover,
    submitCustomTrigger,
    uiZoom,
    orchestrators,
    dispatchRun,
  } = ctx;
  return (
    <>
      {customEvtPopover && (
        <div
          // One popover per run: a different run's popover is a fresh mount, never this one's
          // uncontrolled inputs showing the other's text.
          key={customEvtPopoverRunId}
          {...surface("custom-trigger", customEvtPopoverRunId)}
          {...ui("custom-trigger.dismiss")}
          onClick={() => closeCustomEventPopover(customEvtPopoverRunId)}
          style={{ position: "fixed", inset: 0, zIndex: 500, background: "transparent" }}
        >
          {/* Dot and panel position are divided by uiZoom like the selection box above; the
             date itself goes through clientToLocalX. */}
          <div
            style={{
              position: "fixed",
              left: customEvtPopover.screenX / uiZoom - 5,
              top: customEvtPopover.screenY / uiZoom - 5,
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: C.purple,
              border: `2px solid ${C.purpleL}`,
              zIndex: 502,
              pointerEvents: "none",
              boxShadow: `0 0 8px ${C.purple}`,
            }}
          />
          <div
            {...ui("custom-trigger.dialog")}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              ...customEventPopoverBox({
                screenX: customEvtPopover.screenX,
                screenY: customEvtPopover.screenY,
                uiZoom,
                viewportWidth: window.innerWidth,
              }),
              zIndex: 501,
              background: C.panel,
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              padding: 16,
              width: 300,
              boxShadow: C.shadowBox,
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 600, color: C.white, marginBottom: 4 }}>
              Add a trigger
            </div>
            <div style={{ fontSize: 12, color: C.gray, lineHeight: 1.5, marginBottom: 12 }}>
              Sayer will explore the scenario where it happens.
            </div>

            {customEvtPopover.problem && (
              <div
                data-custom-evt-problem
                role="alert"
                style={{ fontSize: 11, color: C.red, marginBottom: 8 }}
              >
                {customEvtPopover.problem}
              </div>
            )}
            <input
              {...ui("custom-trigger.description")}
              data-custom-evt-desc
              autoFocus
              ref={customEvtDescriptionRef}
              defaultValue={customEvtPopover.description || ""}
              placeholder="Describe what might happen…"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const desc = e.target.value;
                  const dateEl = document.querySelector("[data-custom-evt-date]");
                  const day = dateEl ? dateEl.value : customEvtPopover.date;
                  if (desc.trim())
                    submitCustomTrigger(
                      customEvtPopoverRunId,
                      desc,
                      day,
                      customEvtPopover.dateTime,
                      customEvtPopover,
                    );
                }
                if (e.key === "Escape") closeCustomEventPopover(customEvtPopoverRunId);
              }}
              style={{
                width: "100%",
                background: C.inputBg,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                color: C.white,
                fontFamily: '"IBM Plex Mono",monospace',
                fontSize: 12,
                padding: "6px 8px",
                outline: "none",
                marginBottom: 8,
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: C.gray, letterSpacing: 0 }}>Date</span>
              <input
                {...ui("custom-trigger.date")}
                data-custom-evt-date
                type="date"
                ref={customEvtDateRef}
                defaultValue={customEvtPopover.date}
                style={{
                  background: C.inputBg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.white,
                  fontFamily: '"IBM Plex Mono",monospace',
                  fontSize: 12,
                  padding: "4px 6px",
                  outline: "none",
                  flex: 1,
                }}
              />
            </div>
            <div
              style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}
            >
              <div style={{ flex: 1, minWidth: 0, display: "flex" }}>
                <TriggerDepthSelect
                  orchestrators={orchestrators}
                  tier={customEvtPopover.tier || "quick"}
                  onChange={(t) =>
                    dispatchRun(customEventPopoverEdited(customEvtPopoverRunId, { tier: t }))
                  }
                />
              </div>
              <button
                {...ui("custom-trigger.cancel")}
                onClick={() => closeCustomEventPopover(customEvtPopoverRunId)}
                style={{
                  background: C.btnBg,
                  color: C.gray,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  padding: "4px 10px",
                  fontFamily: '"IBM Plex Mono",monospace',
                  fontSize: 11,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                {...ui("custom-trigger.branch")}
                onClick={() => {
                  const desc = (document.querySelector("[data-custom-evt-desc]") || {}).value || "";
                  const day =
                    (document.querySelector("[data-custom-evt-date]") || {}).value ||
                    customEvtPopover.date;
                  if (desc.trim())
                    submitCustomTrigger(
                      customEvtPopoverRunId,
                      desc,
                      day,
                      customEvtPopover.dateTime,
                      customEvtPopover,
                    );
                }}
                style={{
                  background: C.purple,
                  color: C.btnText || "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "4px 12px",
                  fontFamily: '"IBM Plex Mono",monospace',
                  fontSize: 11,
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                Explore scenario
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
